(function () {
    console.log("🚀 [LQL] LoQally v30.1 - Full Integration (Home + Product + Mobile Sync)");

    if (window.LoQally && window.LoQally.loaded) return;

    window.LoQally = {
        loaded: true,
        state: {
            apiKey: null,
            backendUrl: "https://api.loqally.in/api/v1/public",
            basePriceCents: 0,
            currencySymbol: "₹",
            isFetchingBatch: false,
            isEligible: false,
            isProductPage: false,
            fees: { 'RUSH_LOQALLY': 50, 'EXPRESS': 30, 'STANDARD': 0 }
        },

        init: function (config) {
            if (config.apiKey) this.state.apiKey = config.apiKey.trim();
            this.state.isProductPage = !!config.isProductPage;

            sessionStorage.removeItem('lql_selected_service');
            this.state.isEligible = false;

            // On mobile, touch events fire before click. Shiprocket/GoKwik
            // handle touchend and trigger checkout before our click handler runs.
            // We intercept touchend (capture phase) so we fire first on mobile.
            // A flag prevents double execution when both touchend and click fire.
            this._handledTouch = false;
            const handler = this.handleGlobalClick.bind(this);

            document.addEventListener('touchend', (e) => {
                this._handledTouch = false;
                handler(e);
                if (this._handledTouch) {
                    // Prevent the subsequent click from re-running handleGlobalClick
                    e.target.addEventListener('click', (ce) => {
                        ce.stopImmediatePropagation();
                        ce.preventDefault();
                    }, { once: true, capture: true });
                }
            }, true);
            document.addEventListener('click', (e) => {
                if (this._handledTouch) {
                    this._handledTouch = false;
                    return;
                }
                handler(e);
            }, true);

            this.injectPopupDOM();
            this.cleanForms();

            const savedPincode = sessionStorage.getItem("lql_pincode");
            if (savedPincode) {
                this.injectLocationBar(savedPincode);
                this.refreshServiceability();
                
                // If just updated via popup, scroll to top so pincode bar is visible
                if (sessionStorage.getItem("lql_just_updated") === "true") {
                    sessionStorage.removeItem("lql_just_updated");
                    this.forceScrollToTop();
                } else {
                    this.forceScrollToTop();
                }
            } else {
                this.autoDetectPincode();
            }

            document.addEventListener('variant:change', (event) => {
                if (event.detail.variant) {
                    const newSku = event.detail.variant.id.toString();
                    this.state.currentSku = newSku;
                    this.state.isEligible = false;
                    sessionStorage.removeItem('lql_selected_service');
                    this.cleanForms();
                    this.refreshServiceability(newSku);
                }
            });

            this.observeDOM();
        },

        // ============================================================
        // 🌍 PINCODE & GEOLOCATION
        // Flow: GPS → Popup → IP fallback
        // ============================================================
        autoDetectPincode: function () {
            if (!navigator.geolocation) {
                // No geolocation API — show popup, IP fallback on dismiss
                this.fallbackToPopup();
                return;
            }

            navigator.geolocation.getCurrentPosition(
                async (position) => {
                    try {
                        const { latitude: lat, longitude: lon } = position.coords;
                        const res = await fetch(`https://nominatim.openstreetmap.org/reverse?format=json&lat=${lat}&lon=${lon}`);
                        const data = await res.json();
                        const postal = data?.address?.postcode;

                        if (postal && /^\d{6}$/.test(postal)) {
                            sessionStorage.setItem("lql_pincode", postal);
                            // Reload so the bar is injected during init, before the
                            // theme header renders. Injecting dynamically after page
                            // load puts the bar behind fixed/sticky theme headers.
                            sessionStorage.setItem("lql_just_updated", "true");
                            window.location.reload();
                        } else {
                            // GPS worked but no valid pincode — ask user
                            this.fallbackToPopup();
                        }
                    } catch (e) {
                        console.warn('LoQally reverse geocode fail:', e);
                        this.fallbackToPopup();
                    }
                },
                (error) => {
                    // User denied GPS — show popup to ask pincode manually
                    console.warn('LoQally geolocation denied:', error);
                    this.fallbackToPopup();
                },
                { enableHighAccuracy: true, timeout: 8000 }
            );
        },

        fallbackToPopup: function () {
            setTimeout(() => {
                if (!sessionStorage.getItem("lql_pincode")) this.openPopup();
            }, 500);
        },

        // IP-based pincode detection (used when popup is dismissed without entering pincode)
        detectFromIP: async function () {
            let postal = null;

            // Attempt 1: ipapi
            try {
                const res1 = await fetch('https://ipapi.co/json/');
                const d1 = await res1.json();
                if (d1 && d1.postal) postal = d1.postal;
            } catch (e) { }

            // Attempt 2: ipinfo
            if (!postal) {
                try {
                    const res2 = await fetch('https://ipinfo.io/json');
                    const d2 = await res2.json();
                    if (d2 && d2.postal) postal = d2.postal;
                } catch (e) { }
            }

            // Attempt 3: freeipapi
            if (!postal) {
                try {
                    const res3 = await fetch('https://freeipapi.com/api/json');
                    const d3 = await res3.json();
                    if (d3 && d3.zipCode) postal = d3.zipCode;
                } catch (e) { }
            }

            if (postal) {
                sessionStorage.setItem("lql_pincode", postal);
                this.injectLocationBar(postal);
                this.refreshServiceability();
            }
        },

        // ============================================================
        // 👻 INTERCEPTOR (Shiprocket / GoKwik / Shopify)
        // ============================================================
        handleGlobalClick: function (e) {
            const selectors = '.shopify-payment-button__button, .shopify-payment-button__button--unbranded, .btn-buy-now, .sr-headless-checkout, [onclick*="buyProduct"], [onclick*="buyCart"], [name="checkout"], .cart__checkout-button, [onclick*="buyNowWithShiprocket"], .gokwik-checkout';
            const btn = e.target.closest(selectors);

            if (btn) {
                if (btn.disabled || btn.classList.contains('btn-sold-out')) return;

                const isCartContext = btn.closest('cart-drawer, .cart-drawer, .drawer, .mini-cart, #cart-drawer, .cart__footer, form[action="/cart"]');

                // On homepage/collection: only intercept if the button is inside a
                // product card that has a LoQally badge OR if we're in a cart context.
                // Otherwise, let the native Shiprocket/GoKwik handler do its job.
                if (!this.state.isProductPage && !isCartContext) {
                    const card = btn.closest('product-card, .product-card, .product-item, .card-wrapper, .grid-view-item');
                    const hasBadge = card?.querySelector('.lql-batch-badge');
                    if (!hasBadge) {
                        // No LoQally badge on this card — don't intercept, let native checkout run
                        return;
                    }
                }

                e.preventDefault();
                e.stopPropagation();
                e.stopImmediatePropagation();
                this._handledTouch = true; // Signal touchend handler that we intercepted

                if (isCartContext) {
                    this.executeCartBuy(btn);
                    return;
                }

                let variantId = null;
                const onclick = btn.getAttribute('onclick');
                if (onclick) {
                    const match = onclick.match(/(\d{10,})/);
                    if (match) variantId = match[1];
                }
                if (!variantId) {
                    const form = btn.closest('form') || document.querySelector('form[action*="/cart/add"]');
                    variantId = form?.querySelector('input[name="id"]')?.value;
                }

                if (variantId) {
                    const card = btn.closest('product-card, .product-card, .product-item, .card-wrapper, .grid-view-item');
                    let badgeType = card?.querySelector('.lql-batch-badge')?.getAttribute('data-lql-type');

                    if (card || !this.state.isProductPage) {
                        this.executeCardBuy(variantId, btn, badgeType);
                    } else {
                        this.executeDirectBuy(variantId, btn);
                    }
                } else {
                    this.executeCartBuy(btn);
                }
            }
        },

        triggerCheckout: function (cartAttr, customAttr, variantId, button, originalText, payloadArray = []) {
            const isGoKwik = button.classList.contains('gokwik-checkout') || button.id === 'gokwik-buy-now';
            // Detect Shiprocket globally — if the store has Shiprocket installed, always use it
            const isSR = typeof shiprocketCheckoutEvents !== 'undefined';

            if (isSR && typeof shiprocketCheckoutEvents !== 'undefined') {
                shiprocketCheckoutEvents.buyDirect({
                    type: payloadArray.length > 0 ? "cart" : "product",
                    cartAttributes: cartAttr,
                    products: payloadArray.length > 0 ? payloadArray : [{ variantId, quantity: 1, customAttributes: customAttr }]
                });
                this.resetButton(button, originalText);
                return;
            }

            if (isGoKwik) {
                document.querySelector('.gokwik-checkout, #gokwik-buy-now')?.click();
                this.resetButton(button, originalText);
                return;
            }

            fetch('/cart/update.js', {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ attributes: cartAttr })
            }).finally(() => {
                const pin = sessionStorage.getItem('lql_pincode');
                window.location.href = `/checkout${pin ? '?checkout[shipping_address][zip]=' + pin : ''}`;
            });
        },

        resetButton: function (button, originalText) {
            setTimeout(() => {
                button.disabled = false;
                button.innerText = originalText;
                button.style.opacity = '1';
            }, 3000);
        },

        // ============================================================
        // ⚡ EXECUTION LOGIC
        // ============================================================
        executeCardBuy: function (variantId, button, deliveryType) {
            const originalText = button.innerText;
            button.innerText = 'Processing...';
            button.disabled = true;

            const pincode = sessionStorage.getItem('lql_pincode') || '';
            let tag = (deliveryType === 'RUSH') ? 'RUSH_LOQALLY' : (['SWIFT', 'EXPRESS'].includes(deliveryType) ? 'EXPRESS' : null);

            const attr = { "loqally_pincode": pincode };
            if (tag) attr["loqally_service"] = tag;

            fetch('/cart/add.js', {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ id: variantId, quantity: 1, properties: attr, attributes: attr })
            })
                .then(() => this.triggerCheckout(attr, attr, variantId, button, originalText))
                .catch(() => this.resetButton(button, originalText));
        },

        executeDirectBuy: function (variantId, button) {
            const originalText = button.innerText;
            button.innerText = 'Processing...';
            button.disabled = true;
            const payload = this.buildTagPayload();

            fetch('/cart/add.js', {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ id: variantId, quantity: 1, properties: payload.customAttributes, attributes: payload.cartAttributes })
            })
                .then(() => this.triggerCheckout(payload.cartAttributes, payload.customAttributes, variantId, button, originalText))
                .catch(() => this.resetButton(button, originalText));
        },

        executeCartBuy: function (button) {
            const originalText = button.innerText;
            button.innerText = 'Processing...';
            button.disabled = true;

            fetch('/cart.js').then(res => res.json()).then(cart => {
                const PRIORITY = { 'RUSH_LOQALLY': 3, 'EXPRESS': 2, 'STANDARD': 1 };
                let topSvc = sessionStorage.getItem('lql_selected_service') || '';

                cart.items.forEach(item => {
                    const svc = item.properties?.['loqally_service'];
                    if (svc && (PRIORITY[svc] || 0) > (PRIORITY[topSvc] || 0)) topSvc = svc;
                });

                const attr = { "loqally_pincode": sessionStorage.getItem('lql_pincode') || '', "loqally_service": topSvc };
                fetch('/cart/update.js', {
                    method: 'POST',
                    headers: { 'Content-Type': 'application/json' },
                    body: JSON.stringify({ attributes: attr })
                }).then(() => {
                    const srItems = cart.items.map(i => ({ variantId: i.variant_id, quantity: i.quantity, customAttributes: i.properties }));
                    this.triggerCheckout(attr, {}, null, button, originalText, srItems);
                });
            });
        },

        // ============================================================
        // 🏗️ SERVICEABILITY & UI
        // ============================================================
        refreshServiceability: function (skuOverride) {
            const pin = sessionStorage.getItem("lql_pincode");
            if (!pin) return;

            const targetForm = document.querySelector('.featured-product-form form[action*="/cart/add"]') ||
                document.querySelector('.product-form-block--static form[action*="/cart/add"]') ||
                document.querySelector('.product--add-to-cart-form') ||
                document.querySelector('form[action*="/cart/add"]');

            // PRIORITIZE: 1. Override, 2. Target Form SKU, 3. Analytics, 4. URL
            let sku = skuOverride;
            if (!sku && targetForm) {
                sku = targetForm.querySelector('input[name="id"]')?.value;
            }
            if (!sku) {
                sku = window.ShopifyAnalytics?.meta?.selectedVariantId || new URLSearchParams(window.location.search).get('variant');
            }

            if (sku) {
                const isProductPath = window.location.pathname.includes('/products/');

                if (this.state.isProductPage || isProductPath) {
                    // PRODUCT PAGE PATH
                    this.checkSingleProduct(pin, sku, targetForm);
                } else {
                    // HOMEPAGE / FEATURED PRODUCT PATH
                    if (targetForm) {
                        this.checkFeaturedProduct(pin, sku, targetForm);
                    }
                }
            }

            this.checkBatchServiceability(pin);
        },

        checkSingleProduct: async function (pincode, sku, targetForm) {
            try {
                const res = await fetch(`${this.state.backendUrl}/check-availability`, {
                    method: "POST",
                    headers: { "Content-Type": "application/json", "x-api-key": this.state.apiKey },
                    body: JSON.stringify({ pincode, sku })
                });
                const data = await res.json();
                if (data.serviceable && data.options?.length > 0) {
                    this.renderProductOptions(data, pincode, targetForm);
                } else {
                    this.state.isEligible = false;
                    this.cleanForms();
                }
            } catch (err) { console.error(err); }
        },

        renderProductOptions: function (data, pincode, targetForm) {
            const rushOpt = data.options.find(o => o.delivery_type === 'RUSH');
            const swiftOpt = data.options.find(o => o.delivery_type === 'SWIFT' || o.delivery_type === 'EXPRESS');

            // Target the quantity block as per theme inspect
            const quantityBlock = targetForm?.querySelector('[data-block-id="quantity"]') ||
                document.querySelector('[data-block-id="quantity"]');

            if (!quantityBlock) return;

            this.state.isEligible = true;
            document.getElementById("lql-static-container")?.remove();

            const staticWrapper = document.createElement('div');
            staticWrapper.id = "lql-static-container";
            staticWrapper.className = "lql-delivery-options-static";

            const rPromise = (rushOpt?.delivery_promise || "SOON").toUpperCase();
            const sPromise = swiftOpt?.delivery_promise || "Tomorrow";

            staticWrapper.innerHTML = `
        <div class="lql-header">
            <span>DELIVER TO <strong>${pincode}</strong></span>
            <span class="lql-edit-link" onclick="window.LoQally.openPopup()">CHANGE</span>
        </div>
        <div class="lql-ticker-wrap"><div class="lql-ticker-text">⚡ SAME DAY DELIVERY AVAILABLE • LIVE TRACKING ⚡</div></div>
        <div class="lql-options-list">
            ${rushOpt ? this.generateOptionHtml('RUSH_LOQALLY', 'SUPERFAST', `<span class="lql-time-frame">${rPromise}</span>`, true) : ''}
            ${swiftOpt ? this.generateOptionHtml('EXPRESS', 'EXPRESS', `<span class="lql-time-frame">${sPromise}</span>`, !rushOpt) : ''}
            <label class="lql-option" data-value="STANDARD">
                <div class="lql-option-left"><div class="lql-promise-text">Standard Delivery</div><div class="lql-standard-sub">Within 2-3 Days</div></div>
                <div class="lql-price free">FREE</div>
            </label>
        </div>`;

            quantityBlock.prepend(staticWrapper);

            const options = staticWrapper.querySelectorAll('.lql-option');
            options.forEach(opt => {
                opt.addEventListener('click', () => {
                    options.forEach(o => o.classList.remove('selected'));
                    opt.classList.add('selected');
                    const val = opt.getAttribute('data-value') || opt.querySelector('input')?.value;
                    sessionStorage.setItem('lql_selected_service', val);
                    this.updatePrice(this.state.fees[val] || 0);
                    this.setupFormInputs();
                });
            });
            // Trigger initial state
            staticWrapper.querySelector('.lql-option.selected')?.click();
        },

        generateOptionHtml: function (val, badge, promise, sel) {
            return `
        <label class="lql-option ${sel ? 'selected' : ''}" data-value="${val}">
            <div class="lql-option-left">
                <div class="lql-badge-row"><span class="lql-badge ${badge.toLowerCase()}">${badge}</span></div>
                <div class="lql-promise-text">${promise}</div>
            </div>
            <div class="lql-price free">FREE</div>
            <input type="radio" name="lql_service" value="${val}" ${sel ? 'checked' : ''} style="display:none;">
            <div class="lql-powered-watermark">Powered by <span class="lql-brand">LoQally</span></div>
        </label>`;
        },

        updatePrice: function (addFee) {
            // Scope price updates to the MAIN product only — not collection cards further down the page.
            // Find the closest product section that contains our delivery widget.
            const lqlContainer = document.getElementById('lql-static-container');
            const productSection = lqlContainer?.closest('.product, .product-section, .product-single, [data-section-type="product"], .product-template, .main-product, section') || document;
            
            const priceEls = productSection.querySelectorAll('.product-grid--price, .price-item--regular, .price-item--sale, .product__price .price-item');
            if (this.state.basePriceCents === 0 && priceEls.length > 0) {
                const rawText = priceEls[0].innerText.replace(/[^0-9.]/g, '');
                const parsed = parseFloat(rawText);
                // Guard: only store if we got a real price (not 0 or NaN)
                if (parsed && parsed > 0) {
                    this.state.basePriceCents = parsed * 100;
                }
            }
            // Only update if we have a valid base price captured
            if (this.state.basePriceCents > 0) {
                let final = (this.state.basePriceCents / 100) + addFee;
                priceEls.forEach(el => { el.innerText = this.state.currencySymbol + final.toFixed(2); });
            }
        },

        checkFeaturedProduct: async function (pincode, sku, targetForm) {
            try {
                const res = await fetch(`${this.state.backendUrl}/check-availability`, {
                    method: "POST",
                    headers: { "Content-Type": "application/json", "x-api-key": this.state.apiKey },
                    body: JSON.stringify({ pincode, sku })
                });
                const data = await res.json();
                if (data.serviceable && data.options?.length > 0) {
                    this.renderFeaturedOptions(data, pincode, targetForm);
                }
            } catch (err) { console.error(err); }
        },

        renderFeaturedOptions: function (data, pincode, targetForm) {
            const rushOpt = data.options.find(o => o.delivery_type === 'RUSH');
            const swiftOpt = data.options.find(o => o.delivery_type === 'SWIFT' || o.delivery_type === 'EXPRESS');

            document.getElementById("lql-featured-container")?.remove();

            const featuredWrapper = document.createElement('div');
            featuredWrapper.id = "lql-featured-container";
            featuredWrapper.className = "lql-delivery-options-static";
            featuredWrapper.style.width = "100%";
            featuredWrapper.style.marginTop = "15px";
            featuredWrapper.style.marginBottom = "20px";

            const rPromise = (rushOpt?.delivery_promise || "SOON").toUpperCase();
            const sPromise = swiftOpt?.delivery_promise || "Tomorrow";

            featuredWrapper.innerHTML = `
        <div class="lql-header">
            <span>DELIVER TO <strong>${pincode}</strong></span>
            <span class="lql-edit-link" onclick="window.LoQally.openPopup()">CHANGE</span>
        </div>
        <div class="lql-ticker-wrap"><div class="lql-ticker-text">⚡ SAME DAY DELIVERY AVAILABLE • LIVE TRACKING ⚡</div></div>
        <div class="lql-options-list">
            ${rushOpt ? this.generateOptionHtml('RUSH_LOQALLY', 'SUPERFAST', `<span class="lql-time-frame">${rPromise}</span>`, true) : ''}
            ${swiftOpt ? this.generateOptionHtml('EXPRESS', 'EXPRESS', `<span class="lql-time-frame">${sPromise}</span>`, !rushOpt) : ''}
            <label class="lql-option" data-value="STANDARD">
                <div class="lql-option-left"><div class="lql-promise-text">Standard Delivery</div><div class="lql-standard-sub">Within 2-3 Days</div></div>
                <div class="lql-price free">FREE</div>
            </label>
        </div>`;

            // Insert delivery widget AFTER price section, BEFORE variant/SIZE selector
            const specificDiv = targetForm?.closest('.product--add-to-cart-form') || document.querySelector('.product--add-to-cart-form');

            if (specificDiv) {
                // Find the variant/SIZE picker and insert before it
                const variantPicker = specificDiv.querySelector('variant-radios, variant-selects, .product-form__input, fieldset, .homepage-sections--indiv-product-slider-variants');
                if (variantPicker) {
                    variantPicker.parentNode.insertBefore(featuredWrapper, variantPicker);
                } else {
                    // Fallback: append at end (after price, before buttons)
                    const buyBtnBlock = specificDiv.querySelector('.buy-buttons, .product-form__buttons, [data-block-id="buy_buttons"]');
                    if (buyBtnBlock) {
                        buyBtnBlock.parentNode.insertBefore(featuredWrapper, buyBtnBlock);
                    } else {
                        specificDiv.append(featuredWrapper);
                    }
                }
            } else {
                let injectTarget = targetForm?.querySelector('.homepage-sections--indiv-product-slider-variants') || targetForm;
                if (injectTarget && injectTarget.parentNode) {
                    injectTarget.parentNode.insertBefore(featuredWrapper, injectTarget);
                } else if (injectTarget) {
                    injectTarget.prepend(featuredWrapper);
                }
            }

            const options = featuredWrapper.querySelectorAll('.lql-option');
            options.forEach(opt => {
                opt.addEventListener('click', () => {
                    options.forEach(o => o.classList.remove('selected'));
                    opt.classList.add('selected');
                    const val = opt.getAttribute('data-value') || opt.querySelector('input')?.value;
                    sessionStorage.setItem('lql_selected_service', val);
                });
            });
        },



        // ============================================================
        // 📦 BATCH / COLLECTION PAGE
        // ============================================================
        checkBatchServiceability: async function (pincode) {
            if (this.state.isFetchingBatch) return;
            const cards = document.querySelectorAll(`product-card, .product-card, .product-card-basic, .grid-view-item, .product-item, .card-wrapper`);
            const skus = [];

            cards.forEach(card => {
                if (card.hasAttribute('data-lql-checked')) return;
                let sku = card.getAttribute('data-sku') || card.getAttribute('data-product-id') || card.querySelector('input[name="id"]')?.value;
                if (sku) {
                    skus.push(sku);
                    card.setAttribute('data-lql-sku', sku);
                    card.setAttribute('data-lql-checked', 'true');
                }
            });

            if (skus.length === 0) return;
            this.state.isFetchingBatch = true;
            try {
                const res = await fetch(`${this.state.backendUrl}/check-batch-availability`, {
                    method: "POST",
                    headers: { "Content-Type": "application/json", "x-api-key": this.state.apiKey },
                    body: JSON.stringify({ pincode, items: skus })
                });
                const data = await res.json();
                if (data.success) this.injectBatchBadges(data.results);
            } finally { this.state.isFetchingBatch = false; }
        },

        injectBatchBadges: function (results) {
            document.querySelectorAll('[data-lql-sku]').forEach(card => {
                const sku = card.getAttribute('data-lql-sku');
                const info = results[sku];

                if (info?.serviceable && info.options?.length > 0 && !card.querySelector('.lql-batch-badge')) {
                    const best = info.options.find(o => o.delivery_type === 'RUSH') || info.options[0];
                    const btnRow = card.querySelector('.product-card-buttons');
                    if (!btnRow) return;

                    const badge = document.createElement('div');
                    badge.className = 'lql-batch-badge';
                    badge.setAttribute('data-lql-type', best.delivery_type);
                    badge.innerHTML = `<div class="lql-badge-main">⚡ ${best.delivery_promise.toUpperCase()}</div>`;

                    btnRow.parentNode.insertBefore(badge, btnRow);

                    // DYNAMIC WIDTH MATCHING — desktop only
                    // On mobile (<=767px), CSS handles full-width; inline style would override CSS !important
                    const buyBtn = card.querySelector('.btn-buy-now');
                    if (buyBtn) {
                        let syncTimer = null;
                        const sync = () => {
                            // Skip on mobile — let CSS media query handle width
                            if (window.innerWidth <= 767) {
                                badge.style.removeProperty('width');
                                return;
                            }
                            badge.style.width = buyBtn.getBoundingClientRect().width + "px";
                        };
                        // Debounced sync to avoid intermediate layout values
                        const debouncedSync = () => {
                            clearTimeout(syncTimer);
                            syncTimer = setTimeout(sync, 150);
                        };
                        sync();
                        if (window.ResizeObserver) new ResizeObserver(debouncedSync).observe(buyBtn);
                    }
                }
            });
        },

        // ============================================================
        // 🛠️ UTILS & OBSERVERS
        // ============================================================

        // Robust scroll-to-top that works on iOS Safari & Android Chrome.
        // Mobile browsers restore scroll position AFTER JS runs on reload,
        // so we must: 1) disable scroll restoration, 2) scroll multiple times
        // at staggered intervals to beat the browser's own restore timing.
        forceScrollToTop: function () {
            // Disable browser's automatic scroll restoration on reload
            if ('scrollRestoration' in history) {
                history.scrollRestoration = 'manual';
            }

            const doScroll = () => {
                window.scrollTo(0, 0);
                // Fallback for iOS Safari which sometimes ignores scrollTo
                document.documentElement.scrollTop = 0;
                document.body.scrollTop = 0;
            };

            // Fire immediately
            doScroll();
            // Fire after initial layout
            requestAnimationFrame(doScroll);
            // Fire after short delay (Android Chrome restore point)
            setTimeout(doScroll, 100);
            // Fire after medium delay (iOS Safari restore point)
            setTimeout(doScroll, 300);
            // Fire after full render (catches slow-loading pages)
            setTimeout(doScroll, 800);
        },
        buildTagPayload: function () {
            const pin = sessionStorage.getItem('lql_pincode') || '';
            const attr = { "loqally_pincode": pin };
            if (this.state.isEligible) attr["loqally_service"] = sessionStorage.getItem('lql_selected_service') || 'STANDARD';
            return { customAttributes: attr, cartAttributes: attr };
        },

        cleanForms: function () {
            document.querySelectorAll('form[action*="/cart/add"]').forEach(form => {
                ['properties[loqally_service]', 'attributes[loqally_service]', 'properties[loqally_pincode]'].forEach(name => {
                    form.querySelector(`input[name="${name}"]`)?.remove();
                });
            });
        },

        setupFormInputs: function () {
            if (!this.state.isEligible) { this.cleanForms(); return; }
            document.querySelectorAll('form[action*="/cart/add"]').forEach(form => {
                const payload = this.buildTagPayload();
                const ensure = (name, val) => {
                    let inp = form.querySelector(`input[name="${name}"]`);
                    if (!inp) { inp = document.createElement('input'); inp.type = 'hidden'; inp.name = name; form.appendChild(inp); }
                    inp.value = val;
                };
                if (payload.cartAttributes["loqally_pincode"]) ensure('properties[loqally_pincode]', payload.cartAttributes["loqally_pincode"]);
                if (payload.cartAttributes["loqally_service"]) ensure('properties[loqally_service]', payload.cartAttributes["loqally_service"]);
            });
        },

        injectPopupDOM: function () {
            if (document.getElementById("lql-popup-overlay")) return;
            const html = `<div id="lql-popup-overlay" class="lql-popup-overlay" onclick="if(event.target === this) window.LoQally.closePopup()">
        <div class="lql-popup-box">
          <h2 class="lql-title">Select Location</h2>
          <p class="lql-subtitle">Enter pincode for fastest delivery</p>
          <input type="tel" id="lql-pincode-input" class="lql-input" placeholder="6-digit Pincode" maxlength="6">
          <button class="lql-btn" onclick="window.LoQally.updatePincode()">⚡ Check Delivery</button>
          <div id="lql-msg" style="color:red; font-size:12px; margin-top:5px;"></div>
          <button class="lql-close-link" onclick="window.LoQally.closePopup()">Close</button>
          <div class="lql-powered-modal">Powered by <span class="lql-brand">LoQally</span></div>
        </div></div>`;
            document.body.insertAdjacentHTML('beforeend', html);
        },

        openPopup: function () {
            const overlay = document.getElementById("lql-popup-overlay");
            if (overlay) overlay.style.display = "flex";
        },

        closePopup: async function () {
            const overlay = document.getElementById("lql-popup-overlay");
            if (overlay) overlay.style.display = "none";

            // If user dismissed popup without entering pincode, detect from IP and reload
            if (!sessionStorage.getItem("lql_pincode")) {
                await this.detectFromIP();
                // Reload so pincode bar and delivery options render cleanly
                if (sessionStorage.getItem("lql_pincode")) {
                    sessionStorage.setItem("lql_just_updated", "true");
                    window.location.reload();
                }
            }
        },

        updatePincode: function () {
            const pin = document.getElementById("lql-pincode-input").value.trim();
            if (/^\d{6}$/.test(pin)) {
                sessionStorage.setItem("lql_pincode", pin);
                sessionStorage.setItem("lql_just_updated", "true");
                window.location.reload();
            } else {
                document.getElementById("lql-msg").innerText = "Invalid Pincode";
            }
        },

        injectLocationBar: function (pin) {
            let bar = document.getElementById('lql-location-bar');
            if (bar) { document.getElementById('lql-pincode-display').innerText = pin; return; }
            bar = document.createElement('div');
            bar.id = "lql-location-bar";
            bar.className = "lql-location-bar";
            bar.innerHTML = `<div class="lql-bar-content">📍 To: <strong id="lql-pincode-display">${pin}</strong> <span onclick="window.LoQally.openPopup()">✎</span></div>`;
            // Prepend to body so bar sits above the website header naturally
            document.body.prepend(bar);
        },

        observeDOM: function () {
            const obs = new MutationObserver(() => {
                clearTimeout(this.timeout);
                this.timeout = setTimeout(() => {
                    const pin = sessionStorage.getItem("lql_pincode");
                    if (this.state.isEligible) this.setupFormInputs();
                    if (pin) this.checkBatchServiceability(pin);
                }, 500);
            });
            obs.observe(document.body, { childList: true, subtree: true });
        }
    };
})();